import { useState } from 'react';
import { booksellerBranding } from '../../config/booksellerBranding';

interface Props {
  booksellerId?: string | null;
  booksellerSlug?: string | null;
  booksellerName?: string | null;
  logoUrl?: string | null;
  size?: 'xs' | 'sm' | 'md';
  className?: string;
}

/**
 * Small origin-indicator pill shown on book cards sourced from an external bookseller.
 * Renders nothing when booksellerId is null (LearnLeaf-native books get no pill).
 *
 * Logo resolution order:
 *   1. logoUrl prop (from API-returned Bookseller.LogoUrl)
 *   2. booksellerBranding.tsx entry matched by slug
 *   3. Fallback: first letter of bookseller name inside a neutral circle
 */
export default function BooksellerPill({
  booksellerId,
  booksellerSlug,
  booksellerName,
  logoUrl,
  size = 'sm',
  className,
}: Props) {
  const [logoBroken, setLogoBroken] = useState(false);

  if (!booksellerId) return null;

  const brand = booksellerSlug ? booksellerBranding[booksellerSlug.toLowerCase()] : undefined;
  const effectiveLogo = logoUrl || brand?.logoUrl;
  const showLogo = Boolean(effectiveLogo) && !logoBroken;
  const name = booksellerName || brand?.name || 'Partner';
  const displayName = name.length > 14 ? `${name.slice(0, 13)}…` : name;

  const isIconOnly = size === 'xs';
  const containerClasses = isIconOnly
    ? 'w-5 h-5 justify-center'
    : (size === 'md' ? 'px-2.5 py-1 text-xs' : 'px-2 py-0.5 text-[11px]');
  const iconSize = size === 'md' ? 'w-4 h-4' : 'w-3 h-3';

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full bg-white/95 text-neutral-700 border border-neutral-200 shadow-sm ${containerClasses} ${className ?? ''}`}
      aria-label={`Source: ${name}`}
    >
      {showLogo && effectiveLogo ? (
        <img
          src={effectiveLogo}
          alt=""
          className={`${iconSize} rounded-sm object-contain`}
          onError={() => setLogoBroken(true)}
        />
      ) : (
        <span
          className={`${iconSize} inline-flex items-center justify-center rounded-sm bg-neutral-200 text-[9px] font-semibold text-neutral-700`}
          aria-hidden="true"
        >
          {name.charAt(0).toUpperCase()}
        </span>
      )}
      {!isIconOnly && (
        <span className="font-medium whitespace-nowrap">{displayName}</span>
      )}
    </span>
  );
}
